import { parse } from "marked";

const CARD_RE =
  /<div\s+(?:_className_|className|class)=["']card-uni["'][^>]*>([\s\S]*?)<\/div>/gi;

const KEYWORD_RE =
  /<p\s+(?:_className_|className|class)=["']keyword["']>([\s\S]*?)<\/p>/gi;

const SECTION_NAMES = {
  caseStudies: ["Case Studies", "Case Study"],
  initiatives: ["Initiatives", "Initiative"],
  keywords: ["Keywords", "Keyword"],
};

const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const headingAlternatives = (names) =>
  names.map((name) => escapeRegExp(name)).join("|");

const cleanInner = (html) =>
  String(html || "")
    .replace(/\s+_key_=\{\d+\}/g, "")
    .replace(/\s+key=\{\d+\}/g, "")
    .replace(/_className_/g, "class")
    .replace(/_key_/g, "key")
    .replace(/className=/g, "class=");

/** Turn typed SSE / HTML card payloads into .card-uni markup for tabs. */
export const normalizeCards = (sectionText) => {
  if (!sectionText) return "";
  const source = String(sectionText)
    .replace(/```html/gi, "")
    .replace(/```/g, "");
  const cards = [];
  const re = new RegExp(CARD_RE.source, "gi");
  let match;
  while ((match = re.exec(source)) !== null) {
    cards.push(
      `<div class="card-uni" style="cursor: pointer;">${cleanInner(match[1])}</div>`,
    );
  }
  return cards.join("\n");
};

/**
 * Pull the body that follows a Case Studies / Initiatives / Keywords heading
 * until the next enrichment heading (or end of text).
 */
export const extractNamedSection = (text, names) => {
  if (!text || !names?.length) return "";
  const nameAlt = headingAlternatives(names);
  const startRe = new RegExp(
    `(?:^|\\n)\\s*#{1,6}\\s*\\[?\\s*(?:${nameAlt})\\s*\\]?\\s*(?:\\n|$)|` +
      `<h[1-6][^>]*>\\s*\\[?\\s*(?:${nameAlt})\\s*\\]?\\s*<\\/h[1-6]>`,
    "i",
  );
  const startMatch = startRe.exec(String(text));
  if (!startMatch) return "";

  const afterStart = String(text).slice(startMatch.index + startMatch[0].length);
  const allSectionNames = [
    ...SECTION_NAMES.caseStudies,
    ...SECTION_NAMES.initiatives,
    ...SECTION_NAMES.keywords,
  ];
  const nextHeadingRe = new RegExp(
    `(?:^|\\n)\\s*#{1,6}\\s*\\[?\\s*(?:${headingAlternatives(allSectionNames)})\\s*\\]?\\s*(?:\\n|$)|` +
      `<h[1-6][^>]*>\\s*\\[?\\s*(?:${headingAlternatives(allSectionNames)})\\s*\\]?\\s*<\\/h[1-6]>`,
    "i",
  );
  const nextMatch = nextHeadingRe.exec(afterStart);
  return (nextMatch ? afterStart.slice(0, nextMatch.index) : afterStart).trim();
};

/**
 * Split Case Studies / Initiatives / Keywords from history or legacy note HTML.
 * Prefer heading-scoped cards so Initiatives never land under Case Studies.
 */
export const extractEnrichmentSections = (text) => {
  const source = String(text || "");
  const caseSection = extractNamedSection(source, SECTION_NAMES.caseStudies);
  const initiativeSection = extractNamedSection(
    source,
    SECTION_NAMES.initiatives,
  );
  const keywordSection = extractNamedSection(source, SECTION_NAMES.keywords);

  const caseStudies = normalizeCards(caseSection);
  const initiatives = normalizeCards(initiativeSection);
  const keywordsFromSection = extractKeywordsFromHtml(keywordSection);
  const keywordsFromFull = extractKeywordsFromHtml(source);
  const keywords = keywordsFromSection.length
    ? keywordsFromSection
    : keywordsFromFull;

  return {
    caseStudies: caseStudies || "",
    initiatives: initiatives || "",
    keywords,
  };
};

export const normalizeKeywordsHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/```html/gi, "")
    .replace(/```/g, "")
    .replace(/_className_/g, "class")
    .replace(/_key_/g, "key")
    .replace(/className=/g, "class=");
};

/** Strip markdown emphasis / fences from a keyword chip label. */
export const cleanKeywordLabel = (value) =>
  String(value || "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[*_`#]+/g, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const extractKeywordsFromHtml = (text) => {
  const keywords = [];
  const re = new RegExp(KEYWORD_RE.source, "gi");
  let match;
  while ((match = re.exec(String(text || ""))) !== null) {
    const clean = cleanKeywordLabel(match[1]);
    if (clean) keywords.push(clean);
  }
  return keywords;
};

/** Strip CS / Initiatives / Keywords if they ever leak into the note stream. */
const stripEnrichmentsFromNote = (text) =>
  String(text || "")
    .replace(new RegExp(CARD_RE.source, "gi"), "")
    .replace(new RegExp(KEYWORD_RE.source, "gi"), "")
    // by sudz - 260930
    .replace(
      /<h[1-6][^>]*>\s*\[?\s*(?:Case Studies?|Initiatives?|Keywords?|Mindmaps?)\s*\]?\s*<\/h[1-6]>/gi,
      "",
    )
    .replace(
      /^#{1,6}\s*\[?\s*(?:Case Studies?|Initiatives?|Keywords?|Mindmaps?)\s*\]?\s*$/gim,
      "",
    )
    .replace(/```html[\s\S]*?```/gi, "")
    .replace(/```html[\s\S]*$/i, "");

const stripLeakedCardsFromHtml = (html) =>
  String(html || "")
    .replace(/<div class="card-uni"[\s\S]*?<\/div>/gi, "")
    .replace(/<p class="country">[\s\S]*?<\/p>/gi, "")
    .replace(/<h2 class="heading">[\s\S]*?<\/h2>/gi, "")
    .replace(/<p class="description">[\s\S]*?<\/p>/gi, "")
    .replace(/<pre><code class="language-html">[\s\S]*?<\/code><\/pre>/gi, "")
    .replace(/<p>\s*html\s*<\/p>/gi, "");

/**
 * Parallel Mentormind only: parse note body + mindmap.
 * Case Studies / Initiatives / Keywords come from typed SSE events — never scraped here.
 */
export const prepareMentorMindResponse = (fullResponse = "") => {
  let remaining = String(fullResponse || "");

  const topic =
    remaining.match(
      /<div class=['"]llm-chat-topic-7777['"]>([\s\S]*?)<\/div>/i,
    )?.[1] || null;
  const markmap =
    remaining.match(
      /<div class=['"]llm-chat-mindmap-7777['"]>([\s\S]*?)<\/div>/i,
    )?.[1] || null;

  remaining = remaining
    .replace(/<div class=['"]llm-chat-topic-7777['"]>[\s\S]*?<\/div>/gi, "")
    .replace(/<div class=['"]llm-chat-mindmap-7777['"]>[\s\S]*?<\/div>/gi, "");

  remaining = stripEnrichmentsFromNote(remaining);

  // // by sudz - 260930 - Fix double bullets (* *, - *, * •, etc.)
  remaining = remaining.replace(/^([ \t]*)[-*+•○][ \t]+[-*+•○][ \t]+/gm, "$1- ");
  remaining = remaining.replace(/^([ \t]*)[-*+•○][ \t]*\n\s*[-*+•○][ \t]+/gm, "$1- ");

  let html = remaining.trim();
  try {
    html = parse(html, { async: false }) || "";
  } catch (error) {
    console.error("Error parsing Mentormind markdown:", error);
  }
  // // by sudz - 260930 - Flatten empty parent list items containing nested <ul> (eliminates • ○)
  while (/<li>\s*<ul>([\s\S]*?)<\/ul>\s*<\/li>/i.test(html)) {
    html = html.replace(/<li>\s*<ul>([\s\S]*?)<\/ul>\s*<\/li>/gi, "$1");
  }
  html = stripLeakedCardsFromHtml(html);

  return {
    html,
    topic,
    markmap,
  };
};

export default prepareMentorMindResponse;
